import React, { useMemo } from 'react';
import cx from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Shield } from 'lucide-react';

const Section: React.FC<{
  Icon: any;
  title: string;
  to: string;
  not?: string;
  isError?: boolean;
  subSections?: { title: string; to: string }[];
}> = ({ Icon, title, to, not, isError, subSections }) => {
  const location = useLocation();

  const isSelected = useMemo(() => {
    if (!location.pathname.includes(to)) {
      return false;
    }
    if (not) {
      return !location.pathname.includes(not);
    }

    return true;
  }, [location.pathname, to, not]);

  return (
    <div className="px-3 mb-1">
      <Link
        to={to}
        className={cx(
          'group flex items-center space-x-3 px-3 py-3 rounded-xl font-medium transition-all duration-200 relative w-full',
          isSelected
            ? 'bg-primary-600 text-white shadow-lg'
            : isError
            ? 'text-danger-600 hover:bg-danger-50'
            : 'text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900',
        )}
      >
        {/* Icône */}
        <div
          className={cx(
            'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
            isSelected
              ? 'bg-white/20'
              : isError
              ? 'bg-danger-100'
              : 'bg-neutral-200 group-hover:bg-neutral-300',
          )}
        >
          <Icon
            size={18}
            className={cx(
              isSelected
                ? 'text-white'
                : isError
                ? 'text-danger-600'
                : 'text-neutral-600 group-hover:text-neutral-700',
            )}
          />
        </div>

        {/* Titre */}
        <span className="flex-1 text-left">{title}</span>

        {/* Badge d'erreur */}
        {isError && (
          <div className="w-6 h-6 bg-danger-500 rounded-full flex items-center justify-center">
            <Shield size={14} className="text-white" />
          </div>
        )}

        {/* Chevron pour sous-sections */}
        {!isError && !!subSections?.length && (
          <div
            className={cx(
              'w-5 h-5 rounded flex items-center justify-center',
              isSelected
                ? 'bg-white/20'
                : 'bg-neutral-200 group-hover:bg-neutral-300',
            )}
          >
            {isSelected ? (
              <ChevronDown
                size={12}
                className={isSelected ? 'text-white' : 'text-neutral-600'}
              />
            ) : (
              <ChevronRight
                size={12}
                className="text-neutral-600 group-hover:text-neutral-700"
              />
            )}
          </div>
        )}
      </Link>

      {/* Sous-sections */}
      {isSelected && !!subSections?.length && (
        <div className="mt-2 ml-6 space-y-1">
          {subSections.map((subSection, i) => {
            const isSubSectionSelected =
              location.pathname.includes(subSection.to) ||
              (i === 0 && location.pathname === to);

            return (
              <Link
                key={i}
                to={subSection.to}
                className={cx(
                  'group flex items-center space-x-3 px-3 py-2 rounded-lg font-medium transition-all duration-200 text-body-sm',
                  isSubSectionSelected
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                )}
              >
                {/* Indicateur de sous-section */}
                <div
                  className={cx(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    isSubSectionSelected
                      ? 'bg-primary-600'
                      : 'bg-neutral-300 group-hover:bg-neutral-400',
                  )}
                />

                {/* Titre de la sous-section */}
                <span className="flex-1">{subSection.title}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Section;
